<template>
    <div class="container mt-5">
        <form @submit.prevent="saveActivity">
            <fieldset :disabled="readOnly">
            <!-- ID and Name Row -->
            <div class="row">
                <div class="col-md-6 mb-3">
                    <material-input id="activity-id" label="ID" v-model="activity.activityid" disabled />
                </div>
                <div class="col-md-6 mb-3">
                    <material-input id="activity-name" label="Name" v-model="activity.activityname" placeholder="Enter activity name" required />
                </div>
            </div>

            <!-- Description -->
            <div class="row">
                <div class="col-md-6 mb-3">
                    <MaterialTextarea label="Description" id="description" name="description" v-model="activity.description"
                        :rows="1" placeholder="Enter description" />
                </div>
            </div>

            <!-- Status and Priority Row -->
            <div class="row">
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Status</label>
                        <HelpTextTooltip helpKey="activity_detail_status" />
                    </div>
                    <material-select v-model="activity.status" :options="statusOptions" />
                </div>
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Priority (1-low, 100=high)</label>
                        <HelpTextTooltip helpKey="activity_detail_priority" />
                    </div>
                    <MaterialSpinnerInput id="activity-priority" v-model="activity.priority" :min="1"
                        :max="100" :read-only="true" />
                </div>
            </div>

            <!-- Start Time and Duration Row -->
            <div class="row">
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Start Time</label>
                        <HelpTextTooltip helpKey="activity_detail_starttime" />
                    </div>
                    <material-input id="activity-start-time" v-model="activity.starttime" type="time" />
                </div>
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Duration (minutes)</label>
                        <HelpTextTooltip helpKey="activity_detail_duration" />
                    </div>
                    <MaterialSpinnerInput id="activity-duration" v-model="activity.duration" />
                </div>
            </div>

            <!-- Recurrence Offset and Last Instance Date Row -->
            <div class="row">
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Recurrence Offset (days)</label>
                        <HelpTextTooltip helpKey="activity_detail_recurrenceoffset" />
                    </div>
                    <MaterialSpinnerInput id="activity-recurrence-offset" v-model="activity.recurrenceoffset"
                        :read-only="true" />
                </div>
                <div class="col-md-6 mb-3">
                    <div class="row flex align-items-end">
                        <div class="col-md-9 mb-3">
                            <MaterialDatePicker id="activity-last-instance-date" label="Last Instance Date" v-model="activity.lastinstancedate"
                                :enableTime="true" dateFormat="Y-m-d\\TH:i" />
                        </div>
                        <div class="col-md-3 mb-3" >
                            <material-button type="button" @click="resetInstanceStartDate" variant="secondary" class="px-4" :title="resetButtonTitle">
                                <i class="material-icons text-lg me-1">refresh</i>
                                Reset
                            </material-button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Toggle Options Row -->
            <div class="row">
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Auto Schedule</label>
                        <HelpTextTooltip helpKey="activity_detail_autosched" />
                    </div>
                    <material-select v-model="activity.autoschedule" :options="booleanOptions" />
                </div>
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Auto Generate</label>
                        <HelpTextTooltip helpKey="activity_detail_autogen" />
                    </div>
                    <material-select v-model="activity.autogenerate" :options="booleanOptions" />
                </div>
            </div>

            <!-- More Toggle Options -->
            <div class="row">
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Auto Substitute</label>
                        <HelpTextTooltip helpKey="activity_detail_autosub" />
                    </div>
                    <material-select v-model="activity.autosubstitute" :options="booleanOptions" />
                </div>
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Publicly Viewable</label>
                        <HelpTextTooltip helpKey="activity_detail_pubview" />
                    </div>
                    <material-select v-model="activity.publicview" :options="booleanOptions" />
                </div>
            </div>

            <!-- Additional Options -->
            <div class="row">
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Send Auto-Scheduler Completion Notification</label>
                        <HelpTextTooltip helpKey="activity_detail_autocomplete" />
                    </div>
                    <material-select v-model="activity.autoschedule_complete_adminalert" :options="booleanOptions" />
                </div>
                <div class="col-md-6 mb-3">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">Send Admin Report</label>
                        <HelpTextTooltip helpKey="activity_detail_send_admin_report" />
                    </div>
                    <material-select v-model="activity.send_admin_report" :options="booleanOptions" />
                </div>
            </div>

            <div class="row">
                <div class="col-md-6 mb-3">
                    <material-input id="activity-location-id" label="Location ID" v-model="activity.locationid" type="number" :readOnly="true" />
                </div>
            </div>

            <!-- Notes -->
            <div class="row">
                <div class="col-md-6 mb-4">
                    <MaterialTextarea label="Notes" id="note" name="note" v-model="activity.note" :rows="2"
                        placeholder="Enter notes" />
                </div>
                <div class="col-md-6 mb-4">
                    <MaterialButton variant="text" color="info" :submit="false" @click="viewAllSchedules">
                        View Public Activities/Events
                    </MaterialButton>
                </div>
            </div>

            <!-- Embeddable public "All Schedules" URL for this activity's location -->
            <div class="row">
                <div class="col-12 mb-4">
                    <div class="d-flex align-items-center gap-2 mb-2">
                        <label class="form-label mb-0">All Schedules URL (embed on your website)</label>
                        <HelpTextTooltip helpKey="activity_detail_allschedulesurl" />
                    </div>
                    <div class="d-flex align-items-center gap-2">
                        <material-input id="activity-all-schedules-url" :modelValue="allSchedulesUrl" :read-only="true" class="flex-grow-1" />
                        <material-button type="button" variant="secondary" class="px-3" :submit="false" title="Copy URL" @click="copyAllSchedulesUrl">
                            <i class="material-icons text-lg">content_copy</i>
                        </material-button>
                    </div>
                </div>
            </div>

            </fieldset>
            <!-- Action Buttons -->
            <div v-if="!readOnly" class="d-flex justify-content-end gap-2">
                <material-button @click="saveActivity" variant="primary" :class="['px-4', hasChanges && !isSaving ? 'pulse-glow' : '']" :disabled="isSaving">
                    <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" role="status"
                        aria-hidden="true"></span>
                    <i v-else class="material-icons text-lg me-1">save</i>
                    {{ isSaving ? 'Saving...' : 'Save' }}
                </material-button>
            </div>
        </form>

        <!-- Reset Modal -->
        <ContentModal v-model="showResetMessage" title="Reset Instance Start Date" :showClose="false">
            <p>
                Are you sure you want to reset the last Activity Instance date to the start date/time of the
                primary recurrence record?
            </p>
            <template #footer>
                <div class="d-flex justify-content-end gap-2">
                    <material-button @click="resetStartDate" variant="secondary" class="px-4" :disabled="isSaving">
                        <i class="material-icons text-lg me-1">check</i> Yes
                    </material-button>
                    <material-button @click="showResetMessage = false" variant="secondary" class="px-4"
                        :disabled="isSaving">
                        <i class="material-icons text-lg me-1">close</i> No
                    </material-button>
                </div>
            </template>
        </ContentModal>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";

import MaterialInput from "../../../components/common/MaterialInput.vue";
import MaterialSelect from "../../../components/common/MaterialSelect.vue";
import MaterialButton from "../../../components/common/MaterialButton.vue";
import MaterialTextarea from "../../../components/common/MaterialTextarea.vue";
import MaterialSpinnerInput from "../../../components/common/MaterialSpinnerInput.vue";
import MaterialDatePicker from "../../../components/common/MaterialDatePicker.vue";
import ContentModal from "../../../components/Modal/ContentModal.vue";
import HelpTextTooltip from "../../../components/common/HelpTextTooltip.vue";
import { useToast } from "vue-toast-notification";
import { getLocationId } from "../../../utils/SessionDataHandler";

defineProps({
    readOnly: { type: Boolean, default: false }
});

const store = useStore();
const route = useRoute();
const router = useRouter();
const toast = useToast();

const isSaving = ref(false);
const showResetMessage = ref(false);
const resetButtonTitle = ref("Reset the last instance generated date value");
const baseline = ref(null);

const hasChanges = computed(() => {
    if (!baseline.value) return false;
    return JSON.stringify(baseline.value) !== JSON.stringify({ ...activity });
});

const booleanOptions = [
    { label: "Yes", value: true },
    { label: "No", value: false },
];

const statusOptions = [
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
];

// Keep keys same as backend
const activity = reactive({
    activityid: "",
    activityname: "",
    description: "",
    starttime: "",
    duration: "",
    note: "",
    locationid: "",
    parentactivityid: "",
    status: "",
    priority: "",
    publicview: false,
    autoschedule: false,
    autosubstitute: false,
    autogenerate: false,
    autoschedule_complete_adminalert: false,
    send_admin_report: false,
    recurrenceoffset: "",
    lastinstancedate: "",
});

onMounted(async () => {
    const activityId = route.params.activityid;
    if (!activityId) return;
    try {
        const data = await store.dispatch("fetchActivityDetails", activityId);
        if (data) {
            const { all_schedules_token: scheduleToken, ...activityFields } = data;
            allSchedulesToken.value = scheduleToken || "";
            Object.assign(activity, activityFields);
            baseline.value = { ...activity };
        }
    } catch (error) {
        console.error("Error loading activity:", error);
    }
});

const saveActivity = async () => {
    isSaving.value = true;
    try {       

        if (activity.starttime && activity.starttime.length === 5) {
            activity.starttime = `${activity.starttime}:00`;
        }

        var updateActivityDetails = await store.dispatch("updateActivityDetails", {
            id: activity.activityid,
            payload: activity,
        });

        if (updateActivityDetails) {
            baseline.value = { ...activity };
            toast.success(updateActivityDetails.message || 'Activity updated successfully!', { position: 'top-right' });
        }

    } catch (error) {
        console.error("Error saving activity:", error);
    } finally {
        isSaving.value = false;
    }
};

const resetInstanceStartDate = () => {
    showResetMessage.value = true;
};

const resetStartDate = async () => {
    showResetMessage.value = false;
    isSaving.value = true;
    try {
        const response = await store.dispatch("resetLastInstanceDate", {
            locationid: activity.locationid,
            activityid: activity.activityid,
        });
        
        // Show message from response in toast
        if (response && response.message) {
            activity.lastinstancedate = response.lastinstancedate
            toast.success(response.message, {
                position: "top-right",
            });
        }
    } catch (error) {
        console.error("Error resetting last instance date:", error);
        toast.error(error.message || "Failed to reset last instance date", {
            position: "top-right",
        });
    } finally {
        isSaving.value = false;
    }
};

// Public, embeddable "All Schedules" URL for this activity's location.
// Uses the encoded location token (same as the Edit Location page), never the raw id.
const allSchedulesToken = ref("")
const allSchedulesUrl = computed(() => {
    if (!allSchedulesToken.value) return ''
    const base = (process.env.VUE_APP_BASE_URL || window.location.origin).replace(/\/+$/, '')
    return `${base}/all-schedules/${encodeURIComponent(allSchedulesToken.value)}`
})

const copyAllSchedulesUrl = () => {
    const url = allSchedulesUrl.value
    if (!url) return
    if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url)
            .then(() => toast.success('All Schedules URL copied to clipboard', { position: 'top-right' }))
            .catch(() => toast.error('Failed to copy URL', { position: 'top-right' }))
    } else {
        toast.info(url, { position: 'top-right' })
    }
}

const viewAllSchedules = () => {
    const identifier = allSchedulesToken.value || activity.locationid || getLocationId()
    router.push(`/all-schedules/${encodeURIComponent(identifier)}`)
}
</script>

<style scoped>
.form-label {
    color: #7b809a;
}

.card {
    border: 1px solid #ddd;
    border-radius: 0.5rem;
}

.card-header {
    border-bottom: 1px solid #ddd;
    padding: 1rem;
}
</style>
